import axios from 'axios';

// API Base URL Configuration:
// - Development: Use localhost:8000 (cookies work cross-origin with SameSite=None)
// - Production (Heroku): Empty string (same origin - Django serves React build)
const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:8000' : '';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get CSRF token from cookies
const getCsrfToken = () => {
  try {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith('csrftoken=')) {
        return decodeURIComponent(trimmed.substring('csrftoken='.length));
      }
    }
  } catch (error) {
    console.error('Error getting CSRF token:', error);
  }
  return null;
};

// Add CSRF token to POST/PUT/DELETE requests
api.interceptors.request.use((config) => {
  if (config.method === 'post' || config.method === 'put' || config.method === 'delete' || config.method === 'patch') {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    } else {
      console.warn('CSRF token not found in cookies for', config.url);
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

export const apiService = {
  // Home/Rankings
  getHome: () => api.get('/rankings/api/home/'),
  
  // User
  getUser: (username, season) => api.get(`/rankings/api/user/${username}/${season ? `?season=${season}` : ''}`),
  getCurrentUser: () => api.get('/rankings/api/current-user/'),
  
  // Teams
  getTeams: (page = 1) => api.get(`/rankings/api/teams/?page=${page}`),
  
  // Rating Graph
  getRatingGraph: (season) => api.get(`/rankings/api/rating-graph/${season ? `?season=${season}` : ''}`),
  
  // Auth
  getCsrfToken: () => api.get('/rankings/api/csrf-token/'),
  login: (username, password, rememberMe = false) => api.post('/rankings/api/login/', { username, password, remember_me: rememberMe }),
  logout: () => api.post('/rankings/api/logout/'),
  register: (username, password, password2, token = null) => {
    const url = token ? `/rankings/api/register/${token}/` : '/rankings/api/register/';
    return api.post(url, { username, password, password2, token });
  },
  validateInviteToken: (token) => api.get(`/rankings/api/register/${token}/`),
  generateInvite: () => api.post('/rankings/api/generate-invite/'),
  
  // Users list
  getUsersList: () => api.get('/rankings/api/users/'),
  
  // Record game
  recordGame: (gameData) => api.post('/rankings/api/record-game/', gameData),

  // Predict game (hypothetical elo changes without recording)
  predictGame: (gameData) => api.post('/rankings/api/predict-game/', gameData),

  // Delete game
  deleteGame: (gameId) => api.delete(`/rankings/api/games/${gameId}/delete/`),
  
  // H2H Statistics
  getH2HStatistics: (username1, username2, season) => api.get(`/rankings/api/h2h/${username1}/${username2}/${season ? `?season=${season}` : ''}`),

  // Team Detail
  getTeamDetail: (teamId, season) => api.get(`/rankings/api/teams/${teamId}/${season ? `?season=${season}` : ''}`),

  // Seasons
  getSeasons: () => api.get('/rankings/api/seasons/'),
  getSeasonLeaderboard: (seasonId) => api.get(`/rankings/api/seasons/${seasonId}/`),

  // Team Bonding leaderboard
  getTeamBonding: () => api.get('/rankings/api/team-bonding/'),
};

export default api;
